import { useUIStore } from '@/config/stores/ui-store';
import { HUB_EVENTS, hubEmitter } from '@/core/realtime';
import { Conversation } from '@/features/conversation';
import { updateConversationInCache } from '@/features/messages/utils/conversation-cache';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect } from 'react';

export interface ConversationUpdateEvent {
  updatedConversation: Partial<{
    conversationId: string;
    conversationName?: string;
    conversationAvatarRef?: string;
    lastMessage?: string;
    lastSeenMessageId?: string;
    lastSeenTimestamp?: string;
    lastMessageContent?: string;
    lastMessageId?: string;
    lastMessageTimestamp?: string;
    lastMessageSenderId?: string;
    unreadCount?: number;
    sortTimestamp?: string;
    userId?: string;
    updatedAt?: string;
  }>;
  updatedByUserId: string;
  updateReason: 'NEW_MESSAGE' | 'CONVERSATION_INFO' | 'MARK_READ';
}

export const useConversationUpdatesEvent = () => {
  const queryClient = useQueryClient();
  const { activeConversation, setActiveConversation } = useUIStore();

  useEffect(() => {
    const handleConversationUpdated = (updateEvent: ConversationUpdateEvent) => {
      const { updatedConversation } = updateEvent;
      const conversationId = updatedConversation.conversationId;

      if (!conversationId) {
        return;
      }

      let updated: Partial<Conversation> = {};
      switch (updateEvent.updateReason) {
        case 'NEW_MESSAGE':
          updated = {
            updatedAt: updatedConversation.updatedAt ? dayjs(updatedConversation.updatedAt) : undefined,
            unreadCount: activeConversation?.conversationId === conversationId ? 0 : updatedConversation.unreadCount,
            lastMessageContent: updatedConversation.lastMessageContent,
            lastMessageSenderId: updatedConversation.lastMessageSenderId,
            lastMessageId: updatedConversation.lastMessageId
          };
          break;
        case 'CONVERSATION_INFO':
          updated = {
            conversationName: updatedConversation.conversationName,
            conversationAvatarRef: updatedConversation.conversationAvatarRef,
            updatedAt: updatedConversation.updatedAt ? dayjs(updatedConversation.updatedAt) : undefined
          };
          break;
        case 'MARK_READ':
          updated = {
            lastSeenMessageId: updatedConversation.lastSeenMessageId,
            lastSeenTimestamp: updatedConversation.lastSeenTimestamp,
            unreadCount: 0,
            userId: updatedConversation.userId,
            updatedAt: updatedConversation.updatedAt ? dayjs(updatedConversation.updatedAt) : undefined
          };
          return;
      }

      if (activeConversation?.conversationId === conversationId && updateEvent.updateReason !== 'NEW_MESSAGE')
        setActiveConversation(
          new Conversation({
            ...activeConversation!,
            ...updated
          })
        );

      // Optimistically update conversation in all matching queries
      updateConversationInCache(queryClient, conversationId, updated);
    };

    // Register hub event handler
    hubEmitter.on(HUB_EVENTS.MESSAGE.CONVERSATION_UPDATED, handleConversationUpdated);

    // Cleanup
    return () => {
      hubEmitter.off(HUB_EVENTS.MESSAGE.CONVERSATION_UPDATED, handleConversationUpdated);
    };
  }, [queryClient, activeConversation, setActiveConversation]);
};
